/**
 * headroom.ts — minimal headroom-style scroll behavior.
 * No external dependency: pins an element near the top of a scroll, unpins
 * it once the user scrolls down past `offset` by more than `tolerance`, and
 * re-pins on scroll up. Toggles .headroom--pinned / .headroom--unpinned;
 * direction (which way an element slides off) is left to CSS via a modifier
 * class on the element.
 */

export interface HeadroomState {
  pinned: boolean;
  y: number;
  lastY: number;
  tolerance: number;
  offset: number;
}

/**
 * Pure decision: given the current pin state and a scroll position, what's
 * the next pin state? Mirrors the DOM wrapper's hysteresis exactly — within
 * `tolerance` of the last sampled position, the previous state is kept.
 */
export function computeHeadroomState({ pinned, y, lastY, tolerance, offset }: HeadroomState): boolean {
  const clampedY = Math.max(y, 0);
  if (clampedY <= offset) return true;
  const delta = clampedY - lastY;
  if (Math.abs(delta) > tolerance) return delta <= 0; // scrolling up → pinned, down → unpinned
  return pinned; // within tolerance: no-op
}

export interface HeadroomOptions {
  tolerance?: number;
  offset?: number;
  throttleMs?: number;
}

export function headroom(el: HTMLElement | null | undefined, options: HeadroomOptions = {}): () => void {
  const { tolerance = 6, offset = 40, throttleMs = 60 } = options;
  if (!el) return () => {};

  let lastY = window.scrollY;
  let pinned = true;
  let lastRun = 0;
  let pending = false;

  function apply(nextPinned: boolean): void {
    pinned = nextPinned;
    el!.classList.toggle("headroom--pinned", pinned);
    el!.classList.toggle("headroom--unpinned", !pinned);
  }

  // Timestamp-throttled rather than rAF-gated: rAF callbacks are suspended
  // on hidden/background tabs, which would otherwise leave the element
  // stuck mid-transition until the tab regains visibility.
  function update(): void {
    lastRun = Date.now();
    pending = false;
    const y = Math.max(window.scrollY, 0);
    const next = computeHeadroomState({ pinned, y, lastY, tolerance, offset });
    if (next !== pinned) apply(next);
    lastY = y;
  }

  function onScroll(): void {
    const now = Date.now();
    if (now - lastRun >= throttleMs) {
      update();
    } else if (!pending) {
      pending = true;
      setTimeout(update, throttleMs - (now - lastRun));
    }
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  el.classList.add("headroom", "headroom--pinned");
  return () => window.removeEventListener("scroll", onScroll);
}
